import { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate, Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { LogOut, User, ShieldCheck, Receipt, FileText, ClipboardList, Bell, Upload, Loader2, MessageCircle } from "lucide-react";
import { PortalAuth, portalApi, formatApiError, PasswordInput, ChangePasswordForm, ForcedPasswordChange } from "@/lib/portalClient";
import { usePageSeo } from "@/lib/pageSeo";

/**
 * Owners portal — private area for booked Pali Hill City customers.
 *
 * Layout:
 *   /portal            — dashboard shell + navigation
 *     /profile         — locked primary + editable applicant + co-applicant
 *     /kyc             — PAN/Aadhar uploads with completeness meter
 *     /receipts        — CRM-uploaded receipts + payment schedule
 *     /drafts          — draft agreement download once KYC complete
 *     /activity        — audit log timeline
 *   /portal/login      — email + password (temp on first login)
 *   /portal/reset      — password reset via emailed link
 *
 * Auth: JWT in httpOnly cookies, single audience=customer.
 */

export default function Portal() {
  return (
    <PortalAuth.Provider>
      <Toaster position="top-center" toastOptions={{ style: { background: "#0B4A3D", color: "#F4F4EF", border: "1px solid rgba(166,206,57,0.4)" } }} />
      <Routes>
        <Route path="/login" element={<PortalLogin />} />
        <Route path="/reset" element={<PortalReset />} />
        <Route element={<PortalShell />}>
          <Route index element={<Navigate to="profile" replace />} />
          <Route path="profile" element={<ProfileTab />} />
          <Route path="kyc" element={<KycTab />} />
          <Route path="receipts" element={<ReceiptsTab />} />
          <Route path="drafts" element={<DraftsTab />} />
          <Route path="activity" element={<ActivityTab />} />
          <Route path="contact" element={<ContactTab />} />
        </Route>
      </Routes>
    </PortalAuth.Provider>
  );
}

// ---------- Login + Reset ---------------------------------------------------
function PortalLogin() {
  usePageSeo({ title: "Sign in to your Pali Hill City owners portal" });
  const { user, refresh } = PortalAuth.useCtx();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  if (user && user !== false) return <Navigate to="/portal" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await portalApi.post("/auth/login", { email, password });
      const u = await refresh();
      if (u?.must_change_password) {
        toast.info("Please set a new password to continue.");
      }
      navigate("/portal/profile");
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-offwhite flex items-center justify-center px-4 py-16" data-testid="portal-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="eyebrow text-teal mb-2">Pali Hill City · Owners Portal</div>
          <h1 className="font-display text-forest text-3xl">Sign in</h1>
        </div>
        <form onSubmit={submit} className="bg-white border border-forest/15 rounded-lg p-8 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-forest/60">Email</label>
            <input
              data-testid="portal-login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full border border-forest/20 rounded-md px-3 py-2 text-forest bg-offwhite focus:outline-none focus:border-forest"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-forest/60">Password</label>
            <PasswordInput testId="portal-login-password" value={password} onChange={setPassword} autoComplete="current-password" />
          </div>
          {error && <div className="text-red-600 text-sm" data-testid="portal-login-error">{error}</div>}
          <button
            data-testid="portal-login-submit"
            disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-forest text-cream font-body text-sm hover:bg-void transition-colors disabled:opacity-50"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            Sign in
          </button>
          <div className="text-center pt-2">
            <Link to="/portal/reset" className="text-teal text-sm underline underline-offset-4">Forgot password?</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

function PortalReset() {
  const loc = useLocation();
  const initial = new URLSearchParams(loc.search).get("token") || "";
  const [token, setToken] = useState(initial);
  const [email, setEmail] = useState("");
  const [np, setNp] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const requestLink = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await portalApi.post("/auth/forgot-password", { email });
      setMsg("If that email is registered, a reset link is on the way.");
    } catch (err) {
      setMsg(formatApiError(err?.response?.data?.detail) || "Please try again shortly.");
    } finally {
      setBusy(false);
    }
  };

  const submitNew = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await portalApi.post("/auth/reset-password", { token, new_password: np });
      toast.success("Password updated. Please sign in.");
      window.location.href = "/portal/login";
    } catch (err) {
      setMsg(formatApiError(err?.response?.data?.detail) || "Reset failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-offwhite flex items-center justify-center px-4 py-16" data-testid="portal-reset-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="eyebrow text-teal mb-2">Pali Hill City · Owners Portal</div>
          <h1 className="font-display text-forest text-3xl">Reset password</h1>
        </div>
        {!token ? (
          <form onSubmit={requestLink} className="bg-white border border-forest/15 rounded-lg p-8 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-forest/60">Registered email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full border border-forest/20 rounded-md px-3 py-2 text-forest bg-offwhite focus:outline-none focus:border-forest" data-testid="portal-reset-email" />
            </div>
            {msg && <div className="text-sm text-forest/70">{msg}</div>}
            <button disabled={busy} data-testid="portal-reset-request" className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-forest text-cream font-body text-sm disabled:opacity-50">Send reset link</button>
            <div className="text-center pt-2">
              <Link to="/portal/login" className="text-teal text-sm underline underline-offset-4">Back to sign in</Link>
            </div>
          </form>
        ) : (
          <form onSubmit={submitNew} className="bg-white border border-forest/15 rounded-lg p-8 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-forest/60">New password (min 8 chars)</label>
              <PasswordInput minLength={8} value={np} onChange={setNp} testId="portal-reset-newpass" autoComplete="new-password" />
            </div>
            {msg && <div className="text-sm text-red-600">{msg}</div>}
            <button disabled={busy} data-testid="portal-reset-submit" className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-forest text-cream font-body text-sm disabled:opacity-50">Set new password</button>
          </form>
        )}
      </div>
    </div>
  );
}

// ---------- Shell (nav + activity + notifications) ---------------------------
function PortalShell() {
  const { user, logout } = PortalAuth.useCtx();
  const [dash, setDash] = useState(null);
  const [tick, setTick] = useState(0);
  const loc = useLocation();
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await portalApi.get("/dashboard");
      setDash(data);
    } catch {
      /* auth wrapper handles 401 */
    }
  };

  // Reload on tick change, route change, AND every 10s so the activity
  // timeline reflects CRM actions without requiring a sign-out/sign-in.
  useEffect(() => { load(); }, [tick, loc.pathname]);
  useEffect(() => {
    const iv = setInterval(load, 10000);
    return () => clearInterval(iv);
  }, []);

  if (user === null) return <FullscreenLoader />;
  if (user === false) return <Navigate to="/portal/login" replace />;
  if (!dash) return <FullscreenLoader />;

  // First-login forced password change: hard-block the whole portal until
  // the temporary password is replaced. After that, the change-password
  // option lives only on the Profile tab.
  if (dash?.customer?.must_change_password) {
    return (
      <ForcedPasswordChange
        api={portalApi}
        audienceLabel="Pali Hills: Phase III · Owners Portal"
        testId="portal-forced-change"
        onDone={() => setTick((n) => n + 1)}
        onLogout={logout}
      />
    );
  }

  const tabs = [
    { to: "/portal/profile", label: "Profile", icon: <User size={16} /> },
    { to: "/portal/kyc", label: "KYC", icon: <ShieldCheck size={16} /> },
    { to: "/portal/receipts", label: "Receipts & Payments", icon: <Receipt size={16} /> },
    { to: "/portal/drafts", label: "Draft Agreement", icon: <FileText size={16} /> },
    { to: "/portal/activity", label: "Activity", icon: <ClipboardList size={16} /> },
    { to: "/portal/contact", label: "Contact Us", icon: <MessageCircle size={16} /> },
  ];

  const plots = (dash?.customer?.plot_numbers || []).join(", ");
  const unread = dash?.activity?.filter((a) => a.actor === "crm" || a.actor === "system").slice(0, 5).length || 0;

  return (
    <div className="min-h-screen bg-offwhite" data-testid="portal-shell">
      <header className="bg-forest text-cream">
        <div className="container-editorial flex items-center justify-between py-5">
          <div>
            <div className="eyebrow text-lime">Pali Hills: Phase III · Owners Portal</div>
            <div className="font-display text-2xl mt-0.5">{dash?.customer?.name || user?.name || "…"}</div>
            <div className="text-cream/70 text-sm mt-0.5">
              {dash?.customer?.customer_id} · Plot {plots || "…"}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button className="relative" data-testid="portal-notification-bell" onClick={() => navigate("/portal/activity")}>
              <Bell size={18} />
              {unread > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-lime text-forest text-[10px] font-bold rounded-full flex items-center justify-center">
                  {unread}
                </span>
              )}
            </button>
            <button data-testid="portal-logout" onClick={logout} className="inline-flex items-center gap-1 text-sm text-cream/80 hover:text-cream">
              <LogOut size={14} /> Log out
            </button>
          </div>
        </div>
      </header>

      <div className="container-editorial grid md:grid-cols-12 gap-8 py-10">
        <aside className="md:col-span-3">
          <nav className="space-y-1" data-testid="portal-nav">
            {tabs.map((t) => {
              const active = loc.pathname.startsWith(t.to);
              return (
                <Link
                  key={t.to}
                  to={t.to}
                  data-testid={`portal-nav-${t.to.split("/").pop()}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors ${active ? "bg-forest text-cream" : "text-forest/70 hover:bg-forest/5"}`}
                >
                  {t.icon} {t.label}
                </Link>
              );
            })}
          </nav>
          {dash?.kyc_completeness_pct != null && (
            <div className="mt-6 bg-white border border-forest/15 rounded-lg p-4" data-testid="portal-kyc-meter">
              <div className="flex items-center justify-between text-xs">
                <span className="text-forest/60">KYC completeness</span>
                <span className="font-body text-forest">{dash.kyc_completeness_pct}%</span>
              </div>
              <div className="h-2 mt-2 bg-forest/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-lime transition-all"
                  style={{ width: `${dash.kyc_completeness_pct}%` }}
                />
              </div>
            </div>
          )}
        </aside>
        <main className="md:col-span-9">
          <Outlet context={{ dash, reload: () => setTick((n) => n + 1) }} />
        </main>
      </div>
    </div>
  );
}

function FullscreenLoader() {
  return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-forest" /></div>;
}

// ---------- Profile tab ----------------------------------------------------
import { useOutletContext } from "react-router-dom";

function ProfileTab() {
  const { dash, reload } = useOutletContext();
  const [form, setForm] = useState({
    primary: {}, co_applicants: [], bank_details: {},
  });
  const [busy, setBusy] = useState(false);
  const [changePw, setChangePw] = useState({ open: false });
  const [uploading, setUploading] = useState("");

  // The shell re-polls /dashboard every 10s for live updates. Without this
  // guard, each poll rebuilt the form from server data and wiped whatever
  // the customer was typing. Only reseed the form when the profile content
  // actually changed (first load, after save, or a CRM-side edit).
  const lastProfileSig = useRef("");
  useEffect(() => {
    if (dash?.profile) {
      const sig = JSON.stringify(dash.profile);
      if (sig === lastProfileSig.current) return;
      lastProfileSig.current = sig;
      // legacy single-co shape → co_applicants list
      const legacy = dash.profile.co_applicant
        ? [{ applicant: dash.profile.co_applicant, relation: dash.profile.co_applicant_relation || "", relation_proof_id: dash.profile.co_applicant_relation_proof_id || "" }]
        : [];
      setForm({
        primary: dash.profile.primary || {},
        co_applicants: dash.profile.co_applicants || legacy,
        bank_details: dash.profile.bank_details || {},
      });
    }
  }, [dash?.profile]);

  const save = async () => {
    setBusy(true);
    try {
      await portalApi.put("/profile", form);
      toast.success("Profile saved");
      reload();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const upload = async (kind, file, category = "profile") => {
    setUploading(kind);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    try {
      const { data } = await portalApi.post(`/uploads/${category}`, fd);
      toast.success("Uploaded");
      if (kind === "photo") {
        setForm((f) => ({ ...f, primary: { ...f.primary, photo_id: data.id } }));
      } else if (kind.startsWith("co_photo_")) {
        const idx = Number(kind.split("_")[2]);
        setForm((f) => ({ ...f, co_applicants: f.co_applicants.map((c, i) => i === idx ? { ...c, applicant: { ...c.applicant, photo_id: data.id } } : c) }));
      } else if (kind.startsWith("relation_proof_")) {
        const idx = Number(kind.split("_")[2]);
        setForm((f) => ({ ...f, co_applicants: f.co_applicants.map((c, i) => i === idx ? { ...c, relation_proof_id: data.id } : c) }));
      } else if (kind === "cancelled_cheque") {
        setForm((f) => ({ ...f, bank_details: { ...(f.bank_details || {}), cancelled_cheque_id: data.id } }));
      }
      reload();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setUploading(""); }
  };

  const removeFile = async (fileId, clear) => {
    try {
      await portalApi.delete(`/files/${fileId}`);
      toast.success("File removed");
      clear?.();
      reload();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const c = dash?.customer;
  return (
    <div className="space-y-6" data-testid="portal-profile-tab">
      <div className="bg-white border border-forest/15 rounded-lg p-6">
        <div className="eyebrow text-teal mb-2">Confirmed booking · Pali Hills: Phase III · locked</div>
        <div className="grid md:grid-cols-4 gap-4 text-sm">
          <LockedField k="Name" v={c?.name} />
          <LockedField k="Email" v={c?.email} />
          <LockedField k="Mobile" v={c?.mobile} />
          <LockedField k="Plot(s)" v={(c?.plot_numbers || []).join(", ")} />
        </div>
      </div>

      <ApplicantEditor
        title="Primary applicant"
        value={form.primary}
        onChange={(next) => setForm((f) => ({ ...f, primary: next }))}
        upload={upload}
        uploading={uploading}
        photoKind="photo"
        onRemoveFile={(fileId) => removeFile(fileId, () => setForm((f) => ({ ...f, primary: { ...f.primary, photo_id: "" } })))}
        showPostal
      />

      {form.co_applicants.map((co, i) => (
        <div key={i} className="space-y-4">
          <ApplicantEditor
            title={`Co-applicant ${i + 1}`}
            value={co.applicant || {}}
            onChange={(next) => setForm((f) => ({ ...f, co_applicants: f.co_applicants.map((c2, idx) => idx === i ? { ...c2, applicant: next } : c2) }))}
            upload={upload}
            uploading={uploading}
            photoKind={`co_photo_${i}`}
            onRemoveFile={(fileId) => removeFile(fileId, () => setForm((f) => ({ ...f, co_applicants: f.co_applicants.map((c2, idx) => idx === i ? { ...c2, applicant: { ...c2.applicant, photo_id: "" } } : c2) })))}
            showEmailMobile
          />
          <div className="bg-white border border-forest/15 rounded-lg p-6 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-forest/60">Relation with co-applicant {i + 1}</label>
              <select
                data-testid={`portal-co-relation-${i}`}
                value={co.relation || ""}
                onChange={(e) => setForm((f) => ({ ...f, co_applicants: f.co_applicants.map((c2, idx) => idx === i ? { ...c2, relation: e.target.value } : c2) }))}
                className="mt-1 w-full border border-forest/20 rounded-md px-3 py-2 text-forest bg-offwhite"
              >
                <option value="">Select…</option>
                <option>Spouse</option><option>Parent</option><option>Sibling</option>
                <option>Child</option><option>Business Partner</option><option>Other</option>
              </select>
            </div>
            <FileRow
              label="Relation proof (marriage certificate, ration card, etc.)"
              currentId={co.relation_proof_id}
              onFile={(f) => upload(`relation_proof_${i}`, f)}
              uploading={uploading === `relation_proof_${i}`}
              testId={`portal-relation-proof-${i}`}
              onRemove={co.relation_proof_id ? () => removeFile(co.relation_proof_id, () => setForm((f) => ({ ...f, co_applicants: f.co_applicants.map((c2, idx) => idx === i ? { ...c2, relation_proof_id: "" } : c2) }))) : undefined}
            />
            <button className="text-sm text-red-600 underline" onClick={() => setForm((f) => ({ ...f, co_applicants: f.co_applicants.filter((_, idx) => idx !== i) }))} data-testid={`portal-co-remove-${i}`}>Remove co-applicant {i + 1}</button>
          </div>
        </div>
      ))}

      {form.co_applicants.length < 4 && (
        <button
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-forest/20 text-forest hover:bg-forest/5"
          onClick={() => setForm((f) => ({ ...f, co_applicants: [...f.co_applicants, { applicant: {}, relation: "", relation_proof_id: "" }] }))}
          data-testid="portal-co-add"
        >
          + Add co-applicant ({form.co_applicants.length}/4)
        </button>
      )}

      <div className="bg-white border border-forest/15 rounded-lg p-6 space-y-4" data-testid="portal-bank-details">
        <div className="eyebrow text-teal">Refund bank details · cancelled cheque</div>
        <p className="text-xs text-forest/60">Used only if the booking is cancelled. Bank account should match the primary applicant.</p>
        <div className="grid md:grid-cols-2 gap-4">
          <Input label="Account holder name" v={form.bank_details?.account_holder} onChange={(x) => setForm((f) => ({ ...f, bank_details: { ...(f.bank_details || {}), account_holder: x } }))} />
          <Input label="Bank name" v={form.bank_details?.bank_name} onChange={(x) => setForm((f) => ({ ...f, bank_details: { ...(f.bank_details || {}), bank_name: x } }))} />
          <Input label="Account number" v={form.bank_details?.account_number} onChange={(x) => setForm((f) => ({ ...f, bank_details: { ...(f.bank_details || {}), account_number: x } }))} />
          <Input label="IFSC" v={form.bank_details?.ifsc} onChange={(x) => setForm((f) => ({ ...f, bank_details: { ...(f.bank_details || {}), ifsc: (x || "").toUpperCase() } }))} maxLength={11} />
        </div>
        <FileRow
          label="Cancelled cheque (PDF/JPG/PNG · 5 MB max)"
          currentId={form.bank_details?.cancelled_cheque_id}
          onFile={(f) => upload("cancelled_cheque", f, "refund_bank")}
          uploading={uploading === "cancelled_cheque"}
          testId="portal-cancelled-cheque"
          onRemove={form.bank_details?.cancelled_cheque_id ? () => removeFile(form.bank_details.cancelled_cheque_id, () => setForm((f) => ({ ...f, bank_details: { ...(f.bank_details || {}), cancelled_cheque_id: "" } }))) : undefined}
        />
      </div>

      <div className="flex items-center justify-between border-t border-forest/10 pt-6">
        <button className="text-sm text-teal underline underline-offset-4" onClick={() => setChangePw({ open: !changePw.open })} data-testid="portal-change-pw-toggle">Change password</button>
        <button data-testid="portal-profile-save" disabled={busy} onClick={save} className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-forest text-cream disabled:opacity-50">
          {busy && <Loader2 size={14} className="animate-spin" />} Save profile
        </button>
      </div>

      {changePw.open && (
        <div className="bg-white border border-forest/15 rounded-lg p-6">
          <ChangePasswordForm api={portalApi} testIdPrefix="portal-change-pw" onDone={() => { setChangePw({ open: false }); reload(); }} />
        </div>
      )}
    </div>
  );
}

function LockedField({ k, v }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-forest/50">{k}</div>
      <div className="mt-1 text-forest font-body">{v || "—"}</div>
    </div>
  );
}

function ApplicantEditor({ title, value = {}, onChange, upload, uploading, photoKind, onRemoveFile, showPostal, showEmailMobile }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="bg-white border border-forest/15 rounded-lg p-6 space-y-4">
      <div className="eyebrow text-teal">{title}</div>
      <div className="grid md:grid-cols-2 gap-4">
        <Input label="Full name (as per ID proof)" v={value.full_name} onChange={(x) => set("full_name", x)} />
        <Input label="Date of birth" type="date" v={value.dob} onChange={(x) => set("dob", x)} />
        <Input label="PAN" v={value.pan} onChange={(x) => set("pan", x.toUpperCase())} maxLength={10} />
        <Input label="Aadhar" v={value.aadhar} onChange={(x) => set("aadhar", x)} maxLength={14} />
        {showEmailMobile && <Input label="Email" v={value.email} onChange={(x) => set("email", x)} />}
        {showEmailMobile && <Input label="Mobile" v={value.mobile} onChange={(x) => set("mobile", x)} />}
        <Input label="Address (as per ID proof)" v={value.address} onChange={(x) => set("address", x)} span={2} />
        {showPostal && <Input label="Postal address (for correspondence, if different)" v={value.postal_address} onChange={(x) => set("postal_address", x)} span={2} />}
        <Input label="Workplace" v={value.workplace} onChange={(x) => set("workplace", x)} />
        <Input label="Workplace address" v={value.workplace_address} onChange={(x) => set("workplace_address", x)} />
      </div>
      {photoKind && upload && (
        <FileRow
          label="Photo (JPG / PNG · max 5 MB)"
          currentId={value.photo_id}
          onFile={(f) => upload(photoKind, f)}
          uploading={uploading === photoKind}
          testId={`portal-photo-upload-${photoKind}`}
          onRemove={value.photo_id && onRemoveFile ? () => onRemoveFile(value.photo_id) : undefined}
        />
      )}
    </div>
  );
}

function Input({ label, v, onChange, type = "text", span = 1, maxLength }) {
  return (
    <div className={span === 2 ? "md:col-span-2" : ""}>
      <label className="text-xs uppercase tracking-widest text-forest/60">{label}</label>
      <input
        type={type}
        maxLength={maxLength}
        value={v || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full border border-forest/20 rounded-md px-3 py-2 text-forest bg-offwhite focus:outline-none focus:border-forest"
      />
    </div>
  );
}

function FileRow({ label, currentId, onFile, uploading, testId, onRemove }) {
  return (
    <div className="border-t border-forest/10 pt-4">
      <div className="text-xs uppercase tracking-widest text-forest/60 mb-2">{label}</div>
      <div className="flex items-center gap-3 flex-wrap">
        <label className="inline-flex items-center gap-2 px-4 py-2 border border-forest/20 rounded-full text-sm text-forest cursor-pointer hover:bg-forest/5" data-testid={testId}>
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          {currentId ? "Replace" : "Upload"}
          <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png,.webp,.heic" onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
        </label>
        {currentId && (
          <>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/portal/files/${currentId}`} target="_blank" rel="noopener noreferrer" className="text-teal text-sm underline underline-offset-4" data-testid={`${testId}-view`}>View</a>
            {onRemove && (
              <button type="button" onClick={onRemove} className="text-red-600 text-sm underline underline-offset-4" data-testid={`${testId}-remove`}>Remove</button>
            )}
          </>
        )}
        {currentId && <span className="text-xs text-forest/60">Uploaded</span>}
      </div>
    </div>
  );
}

// ---------- KYC tab --------------------------------------------------------
function KycTab() {
  const { dash, reload } = useOutletContext();
  const [uploading, setUploading] = useState("");

  const upload = async (kind, file) => {
    setUploading(kind);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    try {
      await portalApi.post("/uploads/kyc", fd);
      toast.success("Uploaded");
      reload();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setUploading(""); }
  };

  const findDoc = (kind) => (dash?.kyc_docs || []).find((d) => d.kind === kind);
  const coList = (() => {
    const p = dash?.profile || {};
    if (p.co_applicants && p.co_applicants.length) return p.co_applicants;
    return p.co_applicant ? [{ applicant: p.co_applicant }] : [];
  })();

  const removeFile = async (id) => {
    try {
      await portalApi.delete(`/files/${id}`);
      toast.success("File removed");
      reload();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const kycRow = (kind, legacyKind, label, testId) => {
    const doc = findDoc(kind) || (legacyKind ? findDoc(legacyKind) : null);
    return (
      <FileRow
        label={label}
        currentId={doc?.id}
        onFile={(f) => upload(kind, f)}
        uploading={uploading === kind}
        testId={testId}
        onRemove={doc ? () => removeFile(doc.id) : undefined}
      />
    );
  };

  return (
    <div className="space-y-6" data-testid="portal-kyc-tab">
      <div className="bg-white border border-forest/15 rounded-lg p-6">
        <div className="eyebrow text-teal mb-2">Primary applicant · KYC</div>
        {kycRow("primary_pan", null, "PAN card (PDF/JPG/PNG · 5 MB max)", "portal-kyc-primary-pan")}
        {kycRow("primary_aadhar", null, "Aadhar card (PDF/JPG/PNG · 5 MB max)", "portal-kyc-primary-aadhar")}
      </div>
      {coList.map((co, i) => (
        <div key={i} className="bg-white border border-forest/15 rounded-lg p-6" data-testid={`portal-kyc-co-${i}`}>
          <div className="eyebrow text-teal mb-2">Co-applicant {i + 1} · KYC</div>
          {kycRow(`co_pan_${i}`, i === 0 ? "co_pan" : null, "PAN card", `portal-kyc-co-${i}-pan`)}
          {kycRow(`co_aadhar_${i}`, i === 0 ? "co_aadhar" : null, "Aadhar card", `portal-kyc-co-${i}-aadhar`)}
        </div>
      ))}
      <div className="text-sm text-forest/60">Files are private. Only you and the Pali Hills: Phase III CRM team can access them.</div>
    </div>
  );
}

// ---------- Receipts + Payments ---------------------------------------------
function ReceiptsTab() {
  const { dash, reload } = useOutletContext();
  const list = dash?.receipts || [];
  const payments = dash?.payments || [];
  const [proofBusy, setProofBusy] = useState("");

  const uploadProof = async (paymentId, file) => {
    setProofBusy(paymentId);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await portalApi.post(`/payments/${paymentId}/proof`, fd);
      toast.success("Payment confirmation uploaded — the CRM team will verify it shortly.");
      reload();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setProofBusy(""); }
  };

  const today = new Date().toISOString().slice(0, 10);
  const dueNow = payments.filter((p) => p.status === "due" && p.due_date <= today);

  return (
    <div className="space-y-6" data-testid="portal-receipts-tab">
      {dueNow.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-5" data-testid="portal-payment-due-banner">
          <div className="eyebrow text-amber-800 mb-2">{dueNow.some((p) => p.due_date === today) ? "Payment due today" : "Payment overdue"}</div>
          <ul className="space-y-1 text-sm text-forest">
            {dueNow.map((p) => (
              <li key={p.id} data-testid={`portal-due-item-${p.id}`}>
                <strong>{p.milestone}</strong> — ₹{Number(p.amount || 0).toLocaleString("en-IN")} · {p.account} · due {p.due_date}
              </li>
            ))}
          </ul>
          <div className="text-xs text-forest/60 mt-2">Please make the payment and upload the confirmation screenshot below. Already paid? Just upload the screenshot to mark it complete.</div>
        </div>
      )}
      <div className="bg-white border border-forest/15 rounded-lg p-6">
        <div className="eyebrow text-teal mb-3">Documents from the CRM team</div>
        {list.length === 0 ? (
          <div className="text-sm text-forest/60">Nothing here yet — the CRM team will upload your booking form, cost sheet and payment receipts here as they're processed.</div>
        ) : (
          <ul className="divide-y divide-forest/10">
            {list.map((d) => (
              <li key={d.id} className="py-3 flex items-center justify-between" data-testid={`portal-receipt-${d.id}`}>
                <div>
                  <div className="text-forest font-body">{d.original_filename}</div>
                  <div className="text-xs text-forest/60">{labelForCategory(d.category)} · uploaded {new Date(d.created_at).toLocaleDateString()}</div>
                </div>
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/portal/files/${d.id}`} target="_blank" rel="noopener noreferrer" className="text-teal text-sm underline underline-offset-4">Download</a>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="bg-white border border-forest/15 rounded-lg p-6">
        <div className="eyebrow text-teal mb-3">Payment schedule</div>
        {payments.length === 0 ? (
          <div className="text-sm text-forest/60">Payment schedule will appear here once the CRM team publishes your cost sheet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-forest/60">
              <tr><th className="py-2">Milestone</th><th className="py-2">Account</th><th className="py-2">Due</th><th className="py-2">Amount</th><th className="py-2">Status</th><th className="py-2 text-right">Receipt / Proof</th></tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id} className="border-t border-forest/10">
                  <td className="py-2">{p.milestone}</td>
                  <td className="py-2 text-forest/70">
                    {p.account || "Towards Agreement Value"}
                    {p.account_details && p.status !== "received" && (
                      <div className="text-xs text-forest/50 mt-1" data-testid={`portal-bank-details-${p.id}`}>
                        {p.account_details.name} · {p.account_details.bank}<br />
                        A/c {p.account_details.account_number} · IFSC {p.account_details.ifsc}
                      </div>
                    )}
                  </td>
                  <td className="py-2 text-forest/70">{p.due_date}</td>
                  <td className="py-2">₹{Number(p.amount || 0).toLocaleString("en-IN")}</td>
                  <td className="py-2">
                    {p.status === "received" ? (
                      <span className="text-green-700">Received</span>
                    ) : p.status === "submitted" ? (
                      <span className="text-amber-700">Confirmation submitted · under verification</span>
                    ) : (
                      <label className="inline-flex items-center gap-2 text-teal underline underline-offset-4 cursor-pointer" data-testid={`portal-proof-upload-${p.id}`}>
                        {proofBusy === p.id ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                        Upload confirmation
                        <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={(e) => e.target.files?.[0] && uploadProof(p.id, e.target.files[0])} />
                      </label>
                    )}
                  </td>
                  <td className="py-2 text-right space-x-3">
                    {p.receipt_id && <a href={`${process.env.REACT_APP_BACKEND_URL}/api/portal/files/${p.receipt_id}`} target="_blank" rel="noopener noreferrer" className="text-teal underline">Receipt</a>}
                    {p.proof_id && <a href={`${process.env.REACT_APP_BACKEND_URL}/api/portal/files/${p.proof_id}`} target="_blank" rel="noopener noreferrer" className="text-teal underline" data-testid={`portal-proof-view-${p.id}`}>My confirmation</a>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function labelForCategory(c) {
  return { receipt: "Payment receipt", cost_sheet: "Cost sheet", booking_form: "Booking form", draft: "Draft agreement", loan_sanction: "Bank loan sanction" }[c] || c;
}

// ---------- Drafts tab (unlocks when KYC 100%) -----------------------------
function DraftsTab() {
  const { dash, reload } = useOutletContext();
  const kycDone = (dash?.kyc_completeness_pct || 0) >= 100;
  const drafts = dash?.drafts || [];
  const loanDocs = dash?.loan_docs || [];
  const [uploadingLoan, setUploadingLoan] = useState(false);

  const uploadLoan = async (file) => {
    setUploadingLoan(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", "loan_sanction");
    try {
      await portalApi.post("/uploads/loan_sanction", fd);
      toast.success("Bank loan sanction letter uploaded");
      reload();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setUploadingLoan(false); }
  };

  const removeLoan = async (id) => {
    try {
      await portalApi.delete(`/files/${id}`);
      toast.success("File removed");
      reload();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  return (
    <div data-testid="portal-drafts-tab" className="space-y-6">
      <div className="bg-white border border-forest/15 rounded-lg p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="eyebrow text-teal">Bank loan sanction letter</div>
          <label className="inline-flex items-center gap-2 px-4 py-2 border border-forest/20 rounded-full text-sm text-forest cursor-pointer hover:bg-forest/5" data-testid="portal-loan-upload">
            {uploadingLoan ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {loanDocs.length === 0 ? "Upload" : "Upload another"}
            <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={(e) => e.target.files?.[0] && uploadLoan(e.target.files[0])} />
          </label>
        </div>
        {loanDocs.length === 0 ? (
          <div className="text-sm text-forest/70">If you're financing your plot, upload the bank sanction letter here once your bank issues it (PDF/JPG/PNG · 5 MB max).</div>
        ) : (
          <ul className="divide-y divide-forest/10">
            {loanDocs.map((d) => (
              <li key={d.id} className="py-2 flex items-center justify-between text-sm" data-testid={`portal-loan-${d.id}`}>
                <div className="text-forest">{d.original_filename}</div>
                <div className="flex items-center gap-4">
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/portal/files/${d.id}`} target="_blank" rel="noopener noreferrer" className="text-teal underline">View</a>
                  <button onClick={() => removeLoan(d.id)} className="text-red-600 underline" data-testid={`portal-loan-remove-${d.id}`}>Remove</button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="bg-white border border-forest/15 rounded-lg p-6">
        <div className="eyebrow text-teal mb-3">Draft agreement</div>
        {!kycDone ? (
          <div className="text-sm text-forest/70">Your draft agreement will unlock here once KYC is 100% complete for all applicants. You're currently at <strong>{dash?.kyc_completeness_pct || 0}%</strong>.</div>
        ) : drafts.length === 0 ? (
          <div className="text-sm text-forest/70">KYC is complete. The CRM team is preparing your draft agreement — you'll get an email the moment it's ready.</div>
        ) : (
          <ul className="divide-y divide-forest/10">
            {drafts.map((d) => (
              <li key={d.id} className="py-3 flex items-center justify-between">
                <div className="text-forest font-body">{d.original_filename}</div>
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/portal/files/${d.id}`} target="_blank" rel="noopener noreferrer" className="text-teal underline underline-offset-4">Download</a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ---------- Activity timeline ---------------------------------------------
function ActivityTab() {
  const { dash } = useOutletContext();
  const items = dash?.activity || [];
  return (
    <div className="bg-white border border-forest/15 rounded-lg p-6" data-testid="portal-activity-tab">
      <div className="eyebrow text-teal mb-3">Activity timeline</div>
      {items.length === 0 ? (
        <div className="text-sm text-forest/60">Nothing yet.</div>
      ) : (
        <ul className="space-y-3">
          {items.map((a) => (
            <li key={a.id} className="flex items-start gap-3">
              <span className={`mt-1 h-2 w-2 rounded-full flex-shrink-0 ${a.actor === "crm" ? "bg-lime" : "bg-teal"}`} />
              <div>
                <div className="text-forest">{a.action}</div>
                <div className="text-xs text-forest/50">{a.actor === "crm" ? "Pali Hill City team" : a.actor === "system" ? "System reminder" : "You"} · {new Date(a.created_at).toLocaleString()}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------- Contact Us / support tickets ------------------------------------
const TICKET_CATEGORIES = ["Payments & receipts", "KYC & documents", "Draft agreement", "Site visit", "Loan & bank", "Other"];

function ContactTab() {
  const { reload } = useOutletContext();
  const [tickets, setTickets] = useState(null);
  const [category, setCategory] = useState(TICKET_CATEGORIES[0]);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await portalApi.get("/tickets");
      setTickets(data);
    } catch { /* silent — list is non-critical */ }
  };
  useEffect(() => {
    load();
    const iv = setInterval(load, 10000); // replies appear without refresh
    return () => clearInterval(iv);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || message.trim().length < 5) {
      return toast.error("Please add a subject and a short message.");
    }
    setBusy(true);
    try {
      await portalApi.post("/tickets", { category, subject: subject.trim(), message: message.trim() });
      toast.success("Ticket raised — our CRM team has been emailed and will reach out shortly.");
      setSubject("");
      setMessage("");
      setCategory(TICKET_CATEGORIES[0]);
      load();
      reload(); // refresh activity timeline
    } catch (e2) {
      toast.error(formatApiError(e2?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="portal-contact-tab">
      <div className="bg-white border border-forest/15 rounded-lg p-6">
        <div className="eyebrow text-teal mb-1">Contact us</div>
        <p className="text-xs text-forest/60 mb-4">Raise a ticket and the Pali Hill City CRM team is emailed instantly. You can also write to admin@palihillcity.com or call +91 844 644 8123.</p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-forest/60">Category</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="mt-1 w-full border border-forest/20 rounded-md px-3 py-2 text-forest bg-offwhite" data-testid="portal-contact-category">
              {TICKET_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-forest/60">Subject</label>
            <input required maxLength={160} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. Question about my July instalment" className="mt-1 w-full border border-forest/20 rounded-md px-3 py-2 text-forest bg-offwhite" data-testid="portal-contact-subject" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-forest/60">Message</label>
            <textarea required minLength={5} maxLength={4000} rows={5} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Describe what you need help with…" className="mt-1 w-full border border-forest/20 rounded-md px-3 py-2 text-forest bg-offwhite" data-testid="portal-contact-message" />
          </div>
          <button disabled={busy} className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-forest text-cream text-sm disabled:opacity-50" data-testid="portal-contact-submit">
            {busy && <Loader2 size={14} className="animate-spin" />} Raise ticket
          </button>
        </form>
      </div>

      <div className="bg-white border border-forest/15 rounded-lg p-6">
        <div className="eyebrow text-teal mb-3">Your tickets</div>
        {tickets === null ? (
          <div className="text-sm text-forest/50">Loading…</div>
        ) : tickets.length === 0 ? (
          <div className="text-sm text-forest/60">No tickets yet.</div>
        ) : (
          <ul className="divide-y divide-forest/10">
            {tickets.map((t) => (
              <li key={t.id} className="py-3" data-testid={`portal-ticket-${t.id}`}>
                <div className="flex items-center justify-between gap-4">
                  <div className="text-forest font-body">{t.subject}</div>
                  <span className={`text-xs px-2.5 py-1 rounded-full flex-shrink-0 ${t.status === "open" ? "bg-amber-100 text-amber-800" : "bg-green-100 text-green-800"}`} data-testid={`portal-ticket-status-${t.id}`}>{t.status === "open" ? "Open" : t.status === "resolved" ? "Resolved" : t.status}</span>
                </div>
                <div className="text-xs text-forest/50 mt-1">{t.category} · {new Date(t.created_at).toLocaleString()}</div>
                <div className="text-sm text-forest/70 mt-1 whitespace-pre-wrap">{t.message}</div>
                {(t.replies || []).length > 0 && (
                  <div className="mt-2 space-y-2">
                    {t.replies.map((r, i) => (
                      <div key={i} className="border-l-2 border-lime bg-offwhite rounded-r-md px-3 py-2" data-testid={`portal-ticket-reply-${t.id}-${i}`}>
                        <div className="text-sm text-forest whitespace-pre-wrap">{r.message}</div>
                        <div className="text-xs text-forest/50 mt-1">Pali Hill City team · {new Date(r.at).toLocaleString()}</div>
                      </div>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

